import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Drawer,
  PrioSpinner,
  Steps,
} from '@prio365/prio365-react-library';
import { Form, notification } from 'antd';
import { isSameDay, parseISO, isValid } from 'date-fns';
import { toDate, toISODateString } from './incomingInvoiceDateUtils';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import { useSelector } from 'react-redux';
import { getUserMe } from '../../../../../apps/main/rootReducer';
import Flex from '../../../../../components/Flex';
import {
  EIncomingInvoiceState,
  IncomingInvoiceBaseFields,
  IncomingInvoiceDto,
  IncomingInvoiceFieldDiff,
  InvoiceKey,
} from '../../../../../models/IncomingInvoices';
import { ProjectId } from '../../../../../models/Types';
import { PrioTheme } from '../../../../../theme/types';
import { makePrioStyles } from '../../../../../theme/utils';
import {
  apiApproveIncomingInvoice,
  apiRejectIncomingInvoice,
  apiRequestApprovalIncomingInvoice,
} from '../../../api';
import { InvoicePdfLoadMode, useInvoicePdf } from './hooks/useInvoicePdf';
import { useSaveAllocationsMutation } from './hooks/useSaveAllocationsMutation';
import { useIncomingInvoiceDrawerContext } from './incomingInvoiceComponents/IncomingInvoiceContextProvider';
import {
  INCOMING_INVOICE_FIELD_PAIRS,
  IncomingInvoiceDifferenceDrawer,
} from './incomingInvoiceComponents/IncomingInvoiceDifferenceDrawer';
import { IncomingInvoiceDrawerActions } from './incomingInvoiceComponents/IncomingInvoiceDrawerActions';
import { IncomingInvoiceDrawerSteps } from './incomingInvoiceComponents/IncomingInvoiceDrawerSteps';
import { IncomingInvoiceOCRTriggerModal } from './incomingInvoiceComponents/IncomingInvoiceOCRTriggerModal';
import IncomingInvoiceStatusPill from './incomingInvoiceComponents/IncomingInvoiceStatusPill';
import IncomingInvoicesUnsavedChangesModal from './incomingInvoiceComponents/IncomingInvoicesUnsavedChangesModal';
import { InvoicePdfPreview } from './incomingInvoiceComponents/InvoicePdfPreview';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  drawerContent: {},
  pdfPreview: {
    maxWidth: '1000px',
    overflow: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '400px',
    flexDirection: 'column',
    gap: theme.spacing.regular,
  },
  drawerHeader: {
    marginBottom: theme.spacing.large,
  },
}));

interface IncomingInvoiceDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSave: (invoice: IncomingInvoiceDto) => void;
  invoice: IncomingInvoiceDto | null;
  projectId: ProjectId;
  isAnalyzing: boolean;
  mode: 'edit' | 'view' | 'review';
  onAfterDrawerClose?: () => void;
  onActionComplete?: () => void;
  hasAnyControllerPermission: boolean;
  hasProjectControllerOnly: boolean;
}

const IncomingInvoiceDrawer: React.FC<IncomingInvoiceDrawerProps> = ({
  visible,
  onClose,
  onSave,
  invoice,
  projectId,
  isAnalyzing,
  mode = 'edit',
  onAfterDrawerClose,
  onActionComplete,
  hasAnyControllerPermission,
  hasProjectControllerOnly,
}) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  const [formInstance] = Form.useForm<IncomingInvoiceDto>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const {
    hasUnsavedChanges,
    setHasUnsavedChanges,
    unsavedChangesModalVisible,
    setUnsavedChangesModalVisible,
    form,
    setForm,
    differences,
    setDifferences,
    differenceModalVisible,
    setDifferenceModalVisible,
    isDirtyAllocations,
    setIsDirtyAllocations,
    invoiceAllocations,
    setInvoiceAllocations,
    setFinancialDocumentId,
    invoiceAmountAllocated,
    allocationsValid,
  } = useIncomingInvoiceDrawerContext();

  const userMe = useSelector(getUserMe);
  const officeId = userMe?.myOfficeId;
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(
    mode === 'review' || mode === 'view' ? 3 : 0
  );
  const [isFormValid, setIsFormValid] = useState(false);

  const [isOCRModalVisible, setIsOCRModalVisible] = useState(false);

  const isInitializingRef = useRef(false);
  const validityTimerRef = useRef<number | null>(null);

  const { pdfUrl, pdfLoading, pdfLoadError, loadInvoicePdf, resetPdf } =
    useInvoicePdf(
      invoice,
      mode === 'review' ? InvoicePdfLoadMode.Office : InvoicePdfLoadMode.Creator
    );

  const incomingInvoiceId = invoice?.incomingInvoiceId || '';

  const saveAllocationsMutation = useSaveAllocationsMutation(() => {
    setIsDirtyAllocations(false);
  });

  const skipAllocationStep = !hasAnyControllerPermission;
  const allocationStepOptional = hasProjectControllerOnly;

  const handleNextStep = useCallback(() => {
    setCurrentStep((s) => {
      if (skipAllocationStep && s === 1) {
        return 3;
      }
      return Math.min(3, s + 1);
    });
  }, [skipAllocationStep]);

  const handlePrevStep = useCallback(() => {
    setCurrentStep((s) => {
      if (skipAllocationStep && s === 3) {
        return 1;
      }
      return Math.max(0, s - 1);
    });
  }, [skipAllocationStep]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const getInitialDate = useCallback(
    (primary: unknown, fallback: unknown): string | undefined =>
      toISODateString(primary) ?? toISODateString(fallback),
    []
  );

  const scheduleUpdateValidity = useCallback(() => {
    if (validityTimerRef.current) window.clearTimeout(validityTimerRef.current);
    validityTimerRef.current = window.setTimeout(() => {
      requestAnimationFrame(async () => {
        const names = Object.keys(form.getFieldsValue(true) ?? {});
        try {
          await form.validateFields(names);
        } catch {
          // intentionally empty – we'll read the actual error state below
        }
        const hasErrors = form
          .getFieldsError(names)
          .some(({ errors }) => errors.length > 0);
        setIsFormValid(!hasErrors);
      });
    }, 150);
  }, [form]);

  const handleValuesChange = useCallback(() => {
    if (isInitializingRef.current) return;
    setHasUnsavedChanges(true);
    scheduleUpdateValidity();
  }, [setHasUnsavedChanges, scheduleUpdateValidity]);

  const checkForDifferences = useCallback(
    (invoice: IncomingInvoiceDto) => {
      const diffs: IncomingInvoiceFieldDiff[] = [];
      setDifferences(null);

      const analyzed = invoice.originalAnalyzationResult ?? null;
      if (!analyzed) return;

      const allKeys = new Set<InvoiceKey>([
        ...(Object.keys(invoice) as InvoiceKey[]),
        ...(Object.keys(analyzed) as InvoiceKey[]),
      ]);

      allKeys.forEach((key) => {
        if (!(key in (invoice as IncomingInvoiceBaseFields))) return;

        const invVal = (invoice as IncomingInvoiceBaseFields)[key];
        let aiVal = (analyzed as IncomingInvoiceBaseFields)[key];

        let analyzedFromPair = false;

        const pair = INCOMING_INVOICE_FIELD_PAIRS[key];
        if (!isValidValue(aiVal) && pair) {
          const aiPairVal = (analyzed as IncomingInvoiceBaseFields)[pair];
          if (isValidValue(aiPairVal)) {
            aiVal = aiPairVal;
            analyzedFromPair = true;
          }
        }

        if (!isValidValue(invVal) && !isValidValue(aiVal)) return;

        let different = false;

        const invDate = toDate(invVal);
        const aiDate = toDate(aiVal);
        if (invDate && aiDate) {
          different = !isSameDay(invDate, aiDate);
        } else {
          different = invVal !== aiVal;
        }

        if (different && isValidValue(invVal) && isValidValue(aiVal)) {
          diffs.push({
            key: key as keyof IncomingInvoiceBaseFields,
            invoiceValue: invVal,
            analyzedValue: aiVal,
            analyzedFromPair,
            decision: {
              value: aiVal,
            },
          });
        }
      });

      if (diffs.length > 0) {
        setDifferences({
          incomingInvoiceId: invoice.incomingInvoiceId,
          diffs,
        });
      }
    },
    [setDifferences]
  );

  const handleClose = useCallback(() => {
    onClose();
    if (isAnalyzing) return;
    resetPdf();
    setCurrentStep(0);
    onAfterDrawerClose?.();
    setInvoiceAllocations([]);
  }, [
    isAnalyzing,
    onClose,
    onAfterDrawerClose,
    resetPdf,
    setInvoiceAllocations,
  ]);

  const isValidValue = (value: unknown): boolean => {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0) ||
      value === 0
    )
      return false;

    const date =
      typeof value === 'string'
        ? parseISO(value)
        : value instanceof Date
          ? value
          : null;
    if (date && isValid(date) && date.getFullYear() <= 1) return false;

    return true;
  };

  const handleSave = useCallback(
    async (_values: any): Promise<void> => {
      if (!invoice) return;
      setIsLoading(true);

      try {
        const dto: IncomingInvoiceDto = {
          incomingInvoiceId: invoice?.incomingInvoiceId || '',
          createdBy: invoice?.createdBy || '',
          createdAt: invoice?.createdAt || new Date(),
          notes: form.getFieldValue('notes') || '',
          originalAnalyzationResult: invoice.originalAnalyzationResult || null,
          fileLocation: invoice?.fileLocation || '',
          approvedBy: invoice?.approvedBy,
          approvedAt: invoice?.approvedAt,
          invoiceNumber: form.getFieldValue('invoiceNumber') || '',
          date: form.getFieldValue('date') || new Date(),
          currency: form.getFieldValue('currency') || 'EUR',
          periodOfPerformanceFrom:
            form.getFieldValue('periodOfPerformanceFrom') || null,
          periodOfPerformanceTo:
            form.getFieldValue('periodOfPerformanceTo') || null,
          cashDiscountPercent: form.getFieldValue('cashDiscountPercent') || 0,
          cashDiscountDays: form.getFieldValue('cashDiscountDays') || 0,
          totalExcludingVat: form.getFieldValue('totalExcludingVat') || 0,
          totalIncludingVat: form.getFieldValue('totalIncludingVat') || 0,
          totalVat: form.getFieldValue('totalVat') || 0,
          amountToTransfer: form.getFieldValue('amountToTransfer') || 0,
          isExported: invoice?.isExported || false,
          incomingInvoiceState:
            invoice?.incomingInvoiceState || EIncomingInvoiceState.Draft,
          sharedWithProjectIds:
            form.getFieldValue('sharedWithProjectIds') || [],
          sharedWithOfficeIds: form.getFieldValue('sharedWithOfficeIds') || [],
          fromOfficeId: form.getFieldValue('fromOfficeId') || '',
          fromOfficeName: form.getFieldValue('fromOfficeName') || '',
          toOfficeId: form.getFieldValue('toOfficeId') || '',
          dueDate: form.getFieldValue('dueDate') || new Date(),
        };

        await onSave(dto);
        checkForDifferences(dto);
      } finally {
        setIsLoading(false);
        setHasUnsavedChanges(false);
      }
    },
    [checkForDifferences, form, invoice, onSave, setHasUnsavedChanges]
  );

  const approveInvoice = useCallback(
    async (force?: boolean) => {
      if (!invoice?.incomingInvoiceId) return;
      if (!force && hasUnsavedChanges) {
        setUnsavedChangesModalVisible(true);
        return;
      }
      setIsLoading(true);
      try {
        const result = await apiApproveIncomingInvoice(
          invoice.incomingInvoiceId,
          invoice.toOfficeId
        );
        if (result.result.ok) {
          notification.success({
            message: t(
              'accounting:incomingInvoices.approve.approveSuccessTitle'
            ),
            description: t(
              'accounting:incomingInvoices.approve.approveSuccessDescription'
            ),
          });
          onActionComplete?.();
        } else {
          notification.error({
            message: t('accounting:incomingInvoices.approve.approveErrorTitle'),
          });
        }
      } catch {
        notification.error({
          message: t('accounting:incomingInvoices.approve.approveErrorTitle'),
        });
      } finally {
        handleClose();
        setHasUnsavedChanges(false);
        setIsLoading(false);
      }
    },
    [
      invoice,
      hasUnsavedChanges,
      setUnsavedChangesModalVisible,
      t,
      onActionComplete,
      handleClose,
      setHasUnsavedChanges,
    ]
  );

  const rejectInvoice = useCallback(async () => {
    if (!invoice?.incomingInvoiceId) return;
    setIsLoading(true);
    try {
      const result = await apiRejectIncomingInvoice(
        invoice.incomingInvoiceId,
        invoice.toOfficeId
      );
      if (result.result.ok) {
        notification.success({
          message: t('accounting:incomingInvoices.reject.rejectSuccessTitle'),
          description: t(
            'accounting:incomingInvoices.reject.rejectSuccessDescription'
          ),
        });
        onActionComplete?.();
      } else {
        notification.error({
          message: t('accounting:incomingInvoices.reject.rejectErrorTitle'),
        });
      }
    } catch {
      notification.error({
        message: t('accounting:incomingInvoices.reject.rejectErrorTitle'),
      });
    } finally {
      handleClose();
      setHasUnsavedChanges(false);
      setIsLoading(false);
    }
  }, [
    invoice?.incomingInvoiceId,
    invoice?.toOfficeId,
    t,
    onActionComplete,
    handleClose,
    setHasUnsavedChanges,
  ]);

  const handleSubmitIncomingInvoice = useCallback(
    async (force?: boolean) => {
      if (!invoice?.incomingInvoiceId) return;
      if (!force && hasUnsavedChanges) {
        setUnsavedChangesModalVisible(true);
        return;
      }
      setIsLoading(true);
      try {
        const result = await apiRequestApprovalIncomingInvoice(
          invoice?.incomingInvoiceId
        );
        if (result.result.ok) {
          notification.success({
            message: t(
              'accounting:incomingInvoices.drawer.notifications.submitSuccessTitle'
            ),
            description: t(
              'accounting:incomingInvoices.drawer.notifications.submitSuccessDescription'
            ),
          });
          onActionComplete?.();
        } else {
          notification.error({
            message: t(
              'accounting:incomingInvoices.drawer.notifications.submitErrorTitle'
            ),
          });
        }
      } catch {
        notification.error({
          message: t(
            'accounting:incomingInvoices.drawer.notifications.submitErrorTitle'
          ),
        });
      } finally {
        handleClose();
        setHasUnsavedChanges(false);
        setIsLoading(false);
      }
    },
    [
      invoice?.incomingInvoiceId,
      hasUnsavedChanges,
      setUnsavedChangesModalVisible,
      t,
      onActionComplete,
      handleClose,
      setHasUnsavedChanges,
    ]
  );

  const handleSaveWithAllocations = useCallback(
    async (navigate: boolean = false) => {
      try {
        await form.validateFields();
        await handleSave(form.getFieldsValue());

        if (isDirtyAllocations) {
          await saveAllocationsMutation.mutateAsync();
        }

        if (navigate) {
          setCurrentStep((s) => Math.min(3, s + 1));
        }
      } catch (error) {
        console.error('Save failed:', error);
      }
    },
    [form, handleSave, isDirtyAllocations, saveAllocationsMutation]
  );

  // --- UI helpers --------------------------------------------------

  const drawerTitle = useMemo(() => {
    switch (mode) {
      case 'review':
        return t('accounting:incomingInvoices.drawerTitles.review');
      case 'edit':
        return t('accounting:incomingInvoices.drawerTitles.edit');
      case 'view':
        return t('accounting:incomingInvoices.drawerTitles.view');
      default:
        return t('accounting:incomingInvoices.drawerTitles.default');
    }
  }, [mode, t]);
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    setForm?.(formInstance);
  }, [formInstance, setForm]);

  useEffect(() => {
    if (mode === 'review' || mode === 'view') setCurrentStep(3);
    else setCurrentStep(0);
  }, [mode]);

  useEffect(() => {
    if (!invoice) return;
    isInitializingRef.current = true;

    const initialInvoiceDate = getInitialDate(
      invoice.date,
      invoice.originalAnalyzationResult?.date
    );

    const initialDueDate = getInitialDate(
      invoice.dueDate,
      invoice.originalAnalyzationResult?.dueDate
    );

    const initialPerformanceFromDate = getInitialDate(
      invoice.periodOfPerformanceFrom,
      invoice.originalAnalyzationResult?.periodOfPerformanceFrom
    );

    const initialPerformanceToDate = getInitialDate(
      invoice.periodOfPerformanceTo,
      invoice.originalAnalyzationResult?.periodOfPerformanceTo
    );

    form.setFieldsValue({
      invoiceNumber:
        invoice.invoiceNumber ||
        invoice.originalAnalyzationResult?.invoiceNumber,
      date: initialInvoiceDate,
      currency:
        invoice.currency ||
        invoice.originalAnalyzationResult?.currency ||
        'EUR',
      totalExcludingVat:
        invoice.totalExcludingVat ||
        invoice.originalAnalyzationResult?.totalExcludingVat,
      totalIncludingVat:
        invoice.totalIncludingVat ||
        invoice.originalAnalyzationResult?.totalIncludingVat,
      amountToTransfer:
        invoice.amountToTransfer ||
        invoice.originalAnalyzationResult?.amountToTransfer ||
        invoice.totalIncludingVat ||
        invoice.originalAnalyzationResult?.totalIncludingVat,
      totalVat: invoice.totalVat || invoice.originalAnalyzationResult?.totalVat,
      periodOfPerformanceFrom: initialPerformanceFromDate,
      periodOfPerformanceTo: initialPerformanceToDate,
      cashDiscountPercent:
        invoice.cashDiscountPercent ??
        invoice.originalAnalyzationResult?.cashDiscountPercent,
      cashDiscountDays:
        invoice.cashDiscountDays ??
        invoice.originalAnalyzationResult?.cashDiscountDays,
      notes: invoice.notes || invoice.originalAnalyzationResult?.notes,
      sharedWithProjectIds:
        invoice.sharedWithProjectIds ||
        invoice.originalAnalyzationResult?.sharedWithProjectIds,
      sharedWithOfficeIds:
        invoice.sharedWithOfficeIds.length > 0
          ? invoice.sharedWithOfficeIds
          : invoice.originalAnalyzationResult?.sharedWithOfficeIds.length > 0
            ? invoice.originalAnalyzationResult.sharedWithOfficeIds
            : [officeId],
      dueDate: initialDueDate,
      fromOfficeId:
        invoice.fromOfficeId ||
        invoice.originalAnalyzationResult?.fromOfficeId ||
        '',
      fromOfficeName:
        invoice.fromOfficeName ||
        invoice.originalAnalyzationResult?.fromOfficeName ||
        '',
      toOfficeId:
        invoice.toOfficeId ||
        invoice.originalAnalyzationResult?.toOfficeId ||
        officeId ||
        '',
    });
    checkForDifferences(invoice);
    setHasUnsavedChanges(false);
    requestAnimationFrame(() => {
      isInitializingRef.current = false;
      scheduleUpdateValidity();
    });
  }, [
    invoice,
    form,
    setHasUnsavedChanges,
    getInitialDate,
    officeId,
    checkForDifferences,
    scheduleUpdateValidity,
  ]);

  useEffect(() => {
    if (visible) requestAnimationFrame(() => scheduleUpdateValidity());
  }, [visible, currentStep, scheduleUpdateValidity]);

  useEffect(() => {
    if (
      visible &&
      invoice?.incomingInvoiceId &&
      invoice.incomingInvoiceState !== EIncomingInvoiceState.Processing
    ) {
      loadInvoicePdf();
    }
  }, [visible, invoice, loadInvoicePdf]);

  useEffect(() => {
    setFinancialDocumentId(incomingInvoiceId);
  }, [incomingInvoiceId, setFinancialDocumentId]);

  useEffect(() => {
    setHasUnsavedChanges(isDirtyAllocations);
  }, [isDirtyAllocations, setHasUnsavedChanges]);
  //#endregion

  return (
    <>
      {isAnalyzing ? (
        <Drawer
          title={t('accounting:incomingInvoices.drawer.processingTitle')}
          visible={visible}
          onClose={handleClose}
          footer={
            <div>
              <Button type="link" onClick={handleClose}>
                {t('accounting:incomingInvoices.drawer.cancel')}
              </Button>
            </div>
          }
        >
          <div className={classes.loadingContainer}>
            <PrioSpinner size="large" />
            <span>
              {t('accounting:incomingInvoices.drawer.processingDescription')}
            </span>
          </div>
        </Drawer>
      ) : (
        <Drawer
          title={drawerTitle}
          visible={visible}
          onClose={handleClose}
          customWidth={1000}
          closable={false}
          headerPosition={mode !== 'view' ? 'left' : 'top'}
          footer={
            <IncomingInvoiceDrawerActions
              mode={mode}
              currentStep={currentStep}
              isLoading={isLoading}
              isFormValid={isFormValid}
              hasUnsavedChanges={hasUnsavedChanges || isDirtyAllocations}
              amountFullyAllocated={invoiceAmountAllocated}
              allocationsValid={allocationsValid}
              hasAllocations={(invoiceAllocations?.length || 0) > 0}
              onPrev={handlePrevStep}
              onNext={handleNextStep}
              onSubmit={() => handleSubmitIncomingInvoice()}
              onApprove={() => approveInvoice()}
              onReject={() => rejectInvoice()}
              onCancel={handleClose}
              onSaveWithAllocations={handleSaveWithAllocations}
              skipAllocationStep={skipAllocationStep}
              allocationStepOptional={allocationStepOptional}
            />
          }
          subheader={
            <>
              {mode !== 'view' && (
                <Steps
                  direction="vertical"
                  items={[
                    {
                      title: t(
                        'accounting:incomingInvoices.drawerSteps.data.headline'
                      ),
                      iconProp: ['fal', 'file-lines'],
                      stepNumber: 0,
                    },
                    {
                      title: t(
                        'accounting:incomingInvoices.drawerSteps.controlling.headline'
                      ),
                      iconProp: ['fal', 'user'],
                      stepNumber: 1,
                    },
                    ...(!skipAllocationStep
                      ? [
                          {
                            title:
                              t(
                                'accounting:incomingInvoices.drawerSteps.allocations.headline'
                              ) +
                              (allocationStepOptional
                                ? ` (${t('common:optional')})`
                                : ''),
                            iconProp: ['fal', 'file-pen'] as [
                              'fal',
                              'file-pen',
                            ],
                            stepNumber: 2,
                          },
                        ]
                      : []),
                    {
                      title: t(
                        'accounting:incomingInvoices.drawerSteps.overview.headline'
                      ),
                      iconProp: ['fal', 'eye'] as ['fal', 'eye'],
                      stepNumber: 3,
                    },
                  ]}
                  current={currentStep}
                  showPointerOnHover={false}
                />
              )}
            </>
          }
          mask={
            <>
              {!differenceModalVisible && (
                <InvoicePdfPreview
                  pdfUrl={pdfUrl}
                  pdfLoading={pdfLoading}
                  pdfLoadError={pdfLoadError}
                  t={t}
                  theme={theme}
                  classes={classes}
                />
              )}
            </>
          }
          maskClosable={false}
        >
          <div className={classes.drawerContent}>
            <Flex.Row
              alignItems="center"
              justifyContent="space-between"
              className={classes.drawerHeader}
            >
              <Flex.Row alignItems="center" childrenGap={16}>
                <div
                  style={{
                    borderRadius: theme.borderRadius.small,
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.colors.base.blue[20],
                  }}
                >
                  <FontAwesomeIcon
                    icon={['fal', 'file-import']}
                    style={{
                      color: theme.colors.application.typography.default,
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: theme.font.fontSize.large,
                    fontWeight: theme.font.fontWeight.bold,
                    color: theme.colors.application.typography.default,
                  }}
                >
                  {t('accounting:incomingInvoices.title.singular')}
                </span>
              </Flex.Row>
              <Flex.Row alignItems="center" childrenGap={theme.spacing.small}>
                {pdfUrl &&
                  !pdfLoadError &&
                  mode === 'edit' &&
                  invoice?.incomingInvoiceState !==
                    EIncomingInvoiceState.MissingFile && (
                    <Button
                      onClick={() => setIsOCRModalVisible(true)}
                      size="small"
                    >
                      {t(
                        'accounting:incomingInvoices.drawer.repeatOCRAnalysis'
                      )}
                    </Button>
                  )}
                {differences?.diffs.length > 0 &&
                  (invoice?.incomingInvoiceState ===
                    EIncomingInvoiceState.Draft ||
                    invoice?.incomingInvoiceState ===
                      EIncomingInvoiceState.MissingFile) && (
                    <Button
                      type="primary"
                      size="small"
                      iconColor={theme.colors.base.yellow.default}
                      iconProp={['fal', 'exclamation-triangle']}
                      style={{
                        color: theme.colors.base.yellow.default,
                        backgroundColor: theme.colors.base.yellow[20],
                      }}
                      onClick={() => setDifferenceModalVisible(true)}
                    >
                      {t('accounting:incomingInvoices.drawer.showDifferences')}
                    </Button>
                  )}
                <IncomingInvoiceStatusPill invoice={invoice} size="default" />
              </Flex.Row>
            </Flex.Row>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              onValuesChange={handleValuesChange}
              preserve
              onFinishFailed={() => setIsFormValid(false)}
            >
              <IncomingInvoiceDrawerSteps
                currentStep={currentStep}
                netAmount={
                  form?.getFieldValue('totalExcludingVat') ||
                  invoice?.totalExcludingVat ||
                  0
                }
                projectId={projectId}
                invoice={invoice}
                hasFile={!!pdfUrl}
                pdfLoading={pdfLoading}
                loadInvoicePdf={loadInvoicePdf}
                onClose={onClose}
                onActionComplete={onActionComplete}
                skipAllocationStep={skipAllocationStep}
                allocationStepOptional={allocationStepOptional}
                mode={mode}
              />
            </Form>
          </div>
        </Drawer>
      )}

      <IncomingInvoiceDifferenceDrawer
        mask={
          <InvoicePdfPreview
            pdfUrl={pdfUrl}
            pdfLoading={pdfLoading}
            pdfLoadError={pdfLoadError}
            t={t}
            theme={theme}
            classes={classes}
          />
        }
        incomingInvoiceId={invoice?.incomingInvoiceId || ''}
      />

      <IncomingInvoiceOCRTriggerModal
        visible={isOCRModalVisible}
        invoice={invoice}
        onDrawerClose={onClose}
        onClose={() => setIsOCRModalVisible(false)}
        onActionComplete={onActionComplete}
      />

      <IncomingInvoicesUnsavedChangesModal
        unsavedChanges={unsavedChangesModalVisible}
        onSaveAndContinue={async () => {
          setUnsavedChangesModalVisible(false);
          const values = await form.validateFields();
          await handleSave(values);

          if (invoice?.incomingInvoiceState === EIncomingInvoiceState.Draft) {
            handleSubmitIncomingInvoice(true);
          } else if (
            invoice?.incomingInvoiceState ===
            EIncomingInvoiceState.ApprovalRequested
          ) {
            approveInvoice(true);
          }
        }}
        onContinueWithoutSaving={() => {
          setUnsavedChangesModalVisible(false);
          if (invoice?.incomingInvoiceState === EIncomingInvoiceState.Draft) {
            handleSubmitIncomingInvoice(true);
          } else if (
            invoice?.incomingInvoiceState ===
            EIncomingInvoiceState.ApprovalRequested
          ) {
            approveInvoice(true);
          }
        }}
      />
    </>
  );
};

export default IncomingInvoiceDrawer;
